import { memo, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Button } from '@monksflow/monks-ui';
import { DownloadIcon, XIcon } from 'lucide-react';
import { getWidgetStores } from '@/entities/widget';
import EditSquare from '@/shared/assets/images/EditSquare.svg';
import Reference from '@/shared/assets/images/Reference.svg';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import {
  ImageGenerationWidgetImage,
  type ImageWidgetFileAsset,
} from '../../../model/image-widget.types';
import type { ImageCardProps } from './ImageCard.types';
import { useTranslation } from 'react-i18next';

export default memo(function ImageCard({
  img,
  resolvedRefFile,
  isInComposer,
  onToggleReference,
  onDownload,
  downloading,
  onEdit,
  isEditDisabled,
  hideOverlay,
  readOnly,
  fitToContainer,
}: ImageCardProps) {
  const { t } = useTranslation('contentTest');
  const { downloadUrl, isLoading } = useGetDownloadUrl(img.assetId);
  // Preview surfaces (Library modal) don't host a composer, so Reference/Edit
  // are no-ops there. Drop them entirely so the pill sizes to just Download.
  // Read `renderMode` via the entities/widget binding so this feature stays
  // off the forbidden `@/app/store/...` import path (FSD boundaries).
  const isPreviewMode = getWidgetStores().widgetStore.useWidgetStore(
    (s) => s.renderMode === 'preview',
  );

  // Reference (push into the chat composer) and Edit both mutate / leave the
  // preview, so hide them in read-only mounts as well — keeping only Download.
  const hideMutatingActions = readOnly || isPreviewMode;

  const refFile = useMemo(() => {
    return { ...resolvedRefFile, url: downloadUrl } as ImageGenerationWidgetImage;
  }, [resolvedRefFile, downloadUrl]);

  const attachableRef = useMemo<ImageWidgetFileAsset | null>(() => {
    if (!resolvedRefFile) return null;
    return {
      ...resolvedRefFile,
      url: downloadUrl || resolvedRefFile.url || '',
    };
  }, [resolvedRefFile, downloadUrl]);

  const isEditButtonDisabled = !attachableRef || isEditDisabled;

  const handleRef = useCallback(() => onToggleReference(refFile), [onToggleReference, refFile]);

  const handleDl = useCallback(() => {
    onDownload(refFile);
  }, [onDownload, refFile]);

  const handleEdit = useCallback(() => {
    onEdit?.(attachableRef);
  }, [onEdit, attachableRef]);

  if (isLoading) return null;

  return (
    <div className={clsx('relative', fitToContainer && 'h-full w-full', !hideOverlay && 'group')}>
      {!hideOverlay && (
        <header
          className="
            flex gap-x-1 absolute top-3 right-3 bg-card p-1.5 rounded-full z-10
            opacity-0 pointer-events-none transition-opacity duration-fast
            group-hover:opacity-100 group-hover:pointer-events-auto
            focus-within:opacity-100
          "
          aria-label={t('imageWidget.imageCard.imageActions')}
        >
          {!hideMutatingActions && (
            <Button
              size="icon-sm"
              variant="ghost"
              roundness="full"
              onClick={handleRef}
              disabled={!resolvedRefFile}
              title={
                isInComposer
                  ? t('imageWidget.imageCard.removeReference')
                  : t('imageWidget.imageCard.reference')
              }
            >
              {isInComposer ? (
                <XIcon />
              ) : (
                <img
                  src={Reference}
                  alt={t('imageWidget.imageCard.reference')}
                  className="size-6"
                />
              )}
            </Button>
          )}
          {!hideMutatingActions && onEdit && (
            <Button
              size="icon-sm"
              variant="ghost"
              roundness="full"
              onClick={handleEdit}
              disabled={isEditButtonDisabled}
              title={t('imageWidget.imageCard.edit')}
            >
              <img src={EditSquare} alt={t('imageWidget.imageCard.edit')} className="size-6" />
            </Button>
          )}
          <Button
            size="icon-sm"
            variant="ghost"
            roundness="full"
            onClick={handleDl}
            disabled={downloading || !downloadUrl}
            title={t('imageWidget.imageCard.download')}
          >
            <DownloadIcon />
          </Button>
        </header>
      )}

      {downloadUrl ? (
        <img
          className={clsx(
            'rounded-2xl block object-contain',
            fitToContainer ? 'w-full h-full' : 'w-full h-auto max-h-[80vh]',
          )}
          src={downloadUrl}
          alt={img.metadata.fileName || ''}
        />
      ) : (
        <div
          className={clsx(
            'rounded-2xl w-full bg-muted',
            fitToContainer ? 'h-full' : 'aspect-square',
          )}
          aria-label={t('imageWidget.imageCard.loadingImage')}
        />
      )}
    </div>
  );
});
